import React from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import reduce from 'lodash/reduce'
import { FormikContext } from 'formik'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconEye from '@assets/images/icons/visible.svg'
import IconEyeCrossed from '@assets/images/icons/invisible.svg'
import VisibilityOutlineIcon from '@assets/images/icons/v2/visibility-outline.svg'
import VisibilityOffOutlineIcon from '@assets/images/icons/v2/visibility-off-outline.svg'
import config from '@/config'
import styles from './password-input.css'

const NOT_EMPTY_MESSAGE = 'Dies ist ein Pflichtfeld'
const INVALID_PASSWORD = 'Dein Passwort muss mindestens 8 Zeichen, einen Kleinbuchstaben und eine Zahl oder einen Großbuchstaben enthalten!'
const PASSWORD_SECURITY_STEPS = ['unsicher', 'ausreichend', 'sicher', 'Fort Knox']

// look of the "show password" toggle: the small eye of the app, or the bigger one of the new brand design
export const SHOW_PASSWORD_ICON_VARIANTS = Object.freeze({
  default: 'default',
  brand: 'brand',
})

class PasswordInput extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      value: props.value || '',
      inputType: 'password',
      isInvalid: false,
      errorMessage: null,
      strength: 1,
      strengthText: 'unsicher',
    }

    this.onInputChange = this.onInputChange.bind(this)
    this.onInputBlur = this.onInputBlur.bind(this)
    this.onShowPasswordClick = this.onShowPasswordClick.bind(this)
  }

  componentWillReceiveProps(newProps) {
    const { errorMessage: newErrorMessage } = newProps
    if (isString(newErrorMessage) && newErrorMessage !== this.state.errorMessage) {
      this.setState({ isInvalid: !isEmpty(newErrorMessage), errorMessage: newErrorMessage })
    }
  }

  validateValue(value) {
    const { required, showStrengthBar } = this.props

    if (required && isEmpty(value.trim())) {
      return { isInvalid: true, errorMessage: NOT_EMPTY_MESSAGE }
    } else if (showStrengthBar && !config.passwordChecks[1].test(value)) {
      return { isInvalid: true, errorMessage: INVALID_PASSWORD }
    } else {
      return { isInvalid: false, errorMessage: null }
    }
  }

  onInputChange(event) {
    const { name, onChange } = this.props
    const { target: { value } } = event

    this.setState({ isInvalid: false, errorMessage: '', value }, () => {
      const strength = reduce(config.passwordChecks, (total, regex) => (regex.test(value) ? total + 1 : total), 0)
      const strengthText = PASSWORD_SECURITY_STEPS[strength - 1]

      const { isInvalid, errorMessage } = this.validateValue(value)

      this.setState({ isInvalid, errorMessage, strength, strengthText }, () => {
        onChange && onChange(value, name, isInvalid)
      })
    })
  }

  onInputBlur(event) {
    const { target: { value } } = event

    const { isInvalid, errorMessage } = this.validateValue(value)

    if (isInvalid) {
      return this.setState({ isInvalid, errorMessage })
    }
  }

  onShowPasswordClick() {
    const { inputType } = this.state
    this.setState({ inputType: inputType === 'text' ? 'password' : 'text' })
  }

  render() {
    const { isInvalid, errorMessage, strength, strengthText, inputType } = this.state
    const { name, label, showStrengthBar, disabled, customClass, useFormik, autoFocus, autoComplete, withShowPassword, showPasswordIconVariant, dataQsId } = this.props

    const field = useFormik && this.context ? this.context.getFieldProps(name) : null

    const strengthSteps = PASSWORD_SECURITY_STEPS.map((step, index) => {
      const activeClass = (strength - 1) >= index ? styles.active : ''
      return (
        <div
          key={`step-${index}`}
          ref={`step-${index}`}
          className={`input_passwordStep ${styles.inputPasswordStep} ${activeClass}`}>
          <span></span>
        </div>
      )
    })

    const strengthBar = showStrengthBar
      ? <div className={`input_passwordBar ${styles.strengthBar}`}>{strengthSteps}</div>
      : null

    // prepare classes
    const invalidClass = isInvalid ? styles.invalid : ''
    const disabledClass = disabled ? styles.disabled : ''
    const inputClass = `${styles.input} ${invalidClass}`

    return (
      <div className={`${styles.container} ${customClass} ${disabledClass}`} data-qs-id={dataQsId}>
        <label className={`${styles.label} ${disabledClass}`}>{label}</label>
        <div className={styles.inputWrapper}>
          <input
            name={name}
            type={inputType}
            className={inputClass}
            onChange={this.onInputChange}
            onBlur={this.onInputBlur}
            autoFocus={autoFocus}
            disabled={disabled}
            {...field}
            ref={this.inputRef}
            autoComplete={autoComplete} />
          {/* the outline eye of the login, 24 px - its colour follows the text colour (currentColor) */}
          {withShowPassword && showPasswordIconVariant === SHOW_PASSWORD_ICON_VARIANTS.brand && (
            <SVGIcon
              onClick={this.onShowPasswordClick}
              className={styles.icon}
              svg={inputType === 'text' ? VisibilityOffOutlineIcon : VisibilityOutlineIcon}
              fill='currentColor'
              width='24px'
              height='24px' />
          )}
          {withShowPassword && showPasswordIconVariant !== SHOW_PASSWORD_ICON_VARIANTS.brand && (
            <SVGIcon
              onClick={this.onShowPasswordClick}
              className={styles.icon}
              svg={inputType === 'text' ? IconEye : IconEyeCrossed}
              width="12px"
              height="12px" />
          )}
        </div>
        {strengthBar}
        <div className={styles.hints}>
          <span className={styles.error}>{isInvalid ? errorMessage : ''}</span>
          {showStrengthBar ? <span className={styles.strengthText}>{strengthText}</span> : null}
        </div>
      </div>
    )
  }
}

PasswordInput.contextType = FormikContext

PasswordInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  showStrengthBar: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  customClass: PropTypes.string,
  autoComplete: PropTypes.string,
  useFormik: PropTypes.bool,
  onChange: PropTypes.func,
  showPasswordIconVariant: PropTypes.oneOf(Object.values(SHOW_PASSWORD_ICON_VARIANTS)),
  dataQsId: PropTypes.string.isRequired,
}

PasswordInput.defaultProps = {
  showPasswordIconVariant: SHOW_PASSWORD_ICON_VARIANTS.default,
  showStrengthBar: false,
  autoFocus: false,
  disabled: false,
  customClass: '',
  autoComplete: 'new-password',
}

export default PasswordInput
